import {
  forwardRef,
  type HTMLAttributes,
  type MutableRefObject,
  useEffect,
} from "react"

interface TriggerProps extends HTMLAttributes<HTMLDivElement> {
  targetTriggerElementRef: MutableRefObject<HTMLElement | undefined>
}

const syncTriggerAttributes = [
  "id",
  "data-state", // tells the button if the menu is open or not
  "aria-expanded",
  "aria-controls",
  "aria-haspopup",
]

/**
 * This component serves as a dummy trigger for radix dialog to be able to open
 * and to position the floating menu relative to the real trigger. The trigger
 * rendered here won't be visible, and the props that would be passed to it are
 * passed to the real trigger via the targetTriggerElementRef prop.
 */
export const PassthroughTrigger = forwardRef<HTMLDivElement, TriggerProps>(
  function PassthroughTrigger(props, ref) {
    const { targetTriggerElementRef, style, ...radixTriggerProps } = props

    useEffect(() => {
      /** Synchronizes the properties passed from radix to the real trigger.
       * This ensures that the real trigger has the same properties as the
       * trigger seen by radix (this dummy one). Those properties are needed to
       * style it depending on the menu state, and also to tie it to the menu
       * with accessibility attributes. */
      if (!targetTriggerElementRef.current) return
      for (const [key, value] of Object.entries(radixTriggerProps)) {
        if (isValidTriggerAttribute(key, value)) {
          setOrRemoveAttribute(targetTriggerElementRef.current, key, value)
        }
      }
    }, [radixTriggerProps, targetTriggerElementRef])

    return <div ref={ref} style={style} />
  },
)

function isValidTriggerAttribute(
  key: string,
  value: unknown,
): value is string | number | boolean {
  if (!syncTriggerAttributes.includes(key)) return false
  return (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  )
}

function setOrRemoveAttribute(
  element: HTMLElement,
  key: string,
  value: string | number | boolean | null | undefined,
) {
  if (value != null && value !== "") {
    element.setAttribute(key, value.toString())
  } else {
    element.removeAttribute(key)
  }
}
